import { useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";
import { getExtraction } from "../../../api/knowledgeData/knowledgeDataApi";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import CopyReference from "../../../ui/content/CopyReference";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import DocumentVersionName from "../../sources/document/DocumentVersionName";
import ResearchSpaceLink from "../ResearchSpaceLink";
import OpenReview from "../results/OpenReview";
import ResultView from "../results/ResultView";
import CurationStateBadge from "./CurationStateBadge";
import LineageNotice from "./LineageNotice";

/**
 * `/data-model/instances/<thread>/<entry>`: one extraction result at its deep-link address
 * (F84 H-21), the address every lens opens beside, in its one view (`ResultView`). The
 * header links its document, its Thread and, while it waits, its review step, the one place
 * it is decided (ADR-0026 slice 3a).
 */
export default function ExtractionPage() {
  const { threadId = "", entry = "" } = useParams();
  const [search] = useSearchParams();
  const revision = search.get("revision");
  const source = `${threadId}#${entry}${revision === null ? "" : `/r${revision}`}`;
  const load = useCallback((signal: AbortSignal) => getExtraction(source, signal), [source]);
  const { state, reload } = useLoad(load);
  const { t } = useTranslation();

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={reload} />;
  const extraction = state.value;
  return (
    <div className="mx-auto max-w-6xl space-y-4 py-4">
      <ExtractionHeader extraction={extraction} />
      <LineageNotice extraction={extraction} />
      <ResultView extraction={extraction} />
    </div>
  );
}

function ExtractionHeader({ extraction }: { extraction: Extraction }) {
  const { t, i18n } = useTranslation();
  // Statements are the user word for instance data (W-05, DR-C-07); their document is in the title.
  const statements = extraction.instanceStatements.length > 0;
  return (
    <header className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="app-title text-xl font-bold">
          {statements ? <Trans i18nKey="data.extraction.statementsFrom" components={{ document: <DocumentVersionName reference={extraction.document} /> }} /> : t("data.extraction.title")}
        </h2>
        <CurationStateBadge state={extraction.state} />
        <CopyReference value={extraction.iri} label={t("data.extraction.copyIri")} />
        <ResearchSpaceLink iri={extraction.iri} />
      </div>
      <p className="app-text-muted flex flex-wrap gap-x-3 text-sm">
        {statements ? null : <DocumentVersionName reference={extraction.document} />}
        <span>
          {t("data.extraction.generatedWhen", {
            generation: t(`data.generation.${extraction.generation.kind}`),
            time: formatTimeAgo(extraction.recordedAt, i18n.resolvedLanguage),
          })}
        </span>
        <Link to={appPaths.thread(extraction.threadId, extraction.outputEntry)} className="app-link">
          {t("data.extraction.openThread")}
        </Link>
        <OpenReview source={extraction.source} state={extraction.state} review={extraction.review} />
      </p>
    </header>
  );
}
